import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from "@angular/core";
import {
  Aangifte,
  BokkzIbDataService,
  IncomeTaxEntity,
  LijfrentePremie,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from "rxjs";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type PremieFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  polisnummer: FormControl<string | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-premies-aov',
  templateUrl: './premies-aov.component.html',
  styleUrls: ['./premies-aov.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    CommonModule,
    SharedUiDetailsModule
  ]
})
export class PremiesAovComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public isPartner = false;
  public isDefAangifte = {} as boolean;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public formGroup = new FormGroup({
    lijfrentePremiesAOV: new FormArray<PremieFormGroup>([]),
  });

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        this.isDefAangifte = entity.taxType === 'DA';
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.formGroup.reset({}, { emitEvent: false });
    this.updateForm(details.box1?.uitgavenVoorInkomensvoorzieningen?.lijfrentePremiesAOV || [], this.lijfrentePremiesAOV);
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.subscribeToBedragChanges(this.lijfrentePremiesAOV);
  }

  private subscribeToBedragChanges(formArray: FormArray<PremieFormGroup>): void {
    const extractBedragen = () => formArray.getRawValue()
      .map(item => item.bedrag)
      .filter((bedrag): bedrag is number => bedrag != null && bedrag !== 0);

    this.changeSubscriptions.add(
      formArray.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => extractBedragen()),
        startWith(extractBedragen()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  get lijfrentePremiesAOV(): FormArray<PremieFormGroup> {
    return this.formGroup.controls.lijfrentePremiesAOV;
  }

  addRij(): void {
    this.lijfrentePremiesAOV.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.lijfrentePremiesAOV.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false})
    this.formGroup.markAsDirty({emitEvent: false})
    this.saveForm();
  }

  createFormGroup(item?: LijfrentePremie): PremieFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
      polisnummer: new FormControl<string | null>(item?.polisnummer ?? null),
    });
  }

  private updateForm(data : LijfrentePremie[], array: FormArray<PremieFormGroup>): void {
    const lijst = data || [];
    const formArray = array;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }
    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const form: LijfrentePremie[] = this.lijfrentePremiesAOV.getRawValue()
      .filter(item => item.omschrijving || item.bedrag !== null)
      .map(item => ({
        omschrijving: item.omschrijving ?? undefined,
        bedrag: item.bedrag ?? undefined,
        polisnummer: item.polisnummer ?? undefined,
      }));

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const update = (aangifte: Aangifte) => ({
        ...aangifte,
        box1: {
          ...aangifte.box1,
          uitgavenVoorInkomensvoorzieningen: {
            ...aangifte.box1?.uitgavenVoorInkomensvoorzieningen,
            lijfrentePremiesAOV: form,
          },
        },
      });

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
      } : update(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}

